import React, { useContext } from 'react';
import { UserContext } from './UserContext';
import { GlobalDiscountContext } from './GlobalDiscountContext';
import './PriceDisplay.css';

/**
 * Displays price with trade account discount and global discount applied if available
 * Shows original price struck through and discounted price next to it
 * Can optionally show a custom discount percentage (e.g., for Product of the Month)
 */
const PriceDisplay = ({ price, className = '', showDiscount = true, showDiscountLabel = true, showGlobalDiscount = true, customDiscountPercentage = null, customDiscountLabel = null }) => {
  const { user } = useContext(UserContext);
  const { applyDiscount } = useContext(GlobalDiscountContext);

  const originalPrice = parseFloat(price || 0);

  // If custom discount is provided, use that directly
  if (customDiscountPercentage !== null && customDiscountPercentage > 0) {
    const displayPrice = originalPrice - (originalPrice * (customDiscountPercentage / 100));
    const savingsPercentage = Math.ceil(customDiscountPercentage);

    return (
      <div className={`price-display-with-discount ${className}`}>
        <div className="price-row">
          <span className="original-price">£{originalPrice.toFixed(2)}</span>
          <span className="discounted-price">£{displayPrice.toFixed(2)}</span>
        </div>
        {showDiscountLabel && (
          <span className="discount-label" title={customDiscountLabel || `${savingsPercentage}% discount`}>
            {savingsPercentage}% OFF
          </span>
        )}
      </div>
    );
  }

  // Apply global discount only if showGlobalDiscount is true
  const globalDiscountInfo = showGlobalDiscount ? applyDiscount(originalPrice) : { hasDiscount: false, discountPercentage: 0 };
  let totalDiscountPercentage = globalDiscountInfo.hasDiscount ? globalDiscountInfo.discountPercentage : 0;
  let discountLabels = [];

  if (globalDiscountInfo.hasDiscount) {
    discountLabels.push(globalDiscountInfo.name);
  }

  // Check if user also has a trade account discount
  const hasTradeDiscount =
    showDiscount &&
    user &&
    user.tradeAccount &&
    user.tradeAccount.status === 'approved' &&
    user.tradeAccount.discount > 0;

  // Add trade discount to total (stacking additively)
  if (hasTradeDiscount) {
    totalDiscountPercentage += user.tradeAccount.discount;
    discountLabels.push(`Trade ${user.tradeAccount.discount}%`);
  }

  // No discounts at all - show regular price
  if (totalDiscountPercentage === 0) {
    return <span className={`price-display ${className}`}>£{originalPrice.toFixed(2)}</span>;
  }

  // Calculate final price with total stacked discount
  const displayPrice = originalPrice - (originalPrice * (totalDiscountPercentage / 100));
  const savingsPercentage = Math.ceil(totalDiscountPercentage);

  return (
    <div className={`price-display-with-discount ${className}`}>
      <div className="price-row">
        <span className="original-price">£{originalPrice.toFixed(2)}</span>
        <span className="discounted-price">£{displayPrice.toFixed(2)}</span>
      </div>
      {showDiscountLabel && (
        <span className="discount-label" title={discountLabels.join(' + ')}>
          {savingsPercentage}% OFF
        </span>
      )}
    </div>
  );
};

export default PriceDisplay;
